import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  CTAButton,
  H2,
  HeroH1,
  InlineLink,
  P,
  SeoPageShell,
} from "./seo/SeoPageLayout";

const WhyDoTheyDisappearAndComeBack = () => {
  return (
    <SeoPageShell>
      <SEO
        title="Why Do They Disappear Then Come Back? (The Push-Pull Cycle)"
        description="They go quiet, then return right when you start to move on. Why the disappear-and-come-back cycle feels uncanny — and why their return doesn't mean what you hope it does."
        path="/why-do-they-disappear-and-come-back"
      />

      <HeroH1>Why They Disappear, Then Come Back Right When You're Moving On</HeroH1>

      <P>
        You finally started to feel okay. The silence stopped feeling like a wound and started
        feeling like space. And then — a text. Warm, like nothing happened. The timing isn't an
        accident, and the confusion you feel isn't yours to carry.
      </P>
      <P>
        If the cycle has been wearing you down,{" "}
        <InlineLink to="/why-do-i-feel-exhausted-after-conversations">
          why these conversations leave you so exhausted →
        </InlineLink>
      </P>

      <H2>The silence isn't absence — it's part of the pattern</H2>
      <P>
        When someone goes quiet and then returns, it's easy to read the disappearance as the
        problem and the return as the relief. But the two work together. The distance makes you
        anxious; the return makes you grateful. Over time your nervous system learns to treat the
        return as the reward — which means you stop noticing that the disappearance was the thing
        that hurt.
      </P>

      <H2>Why the timing feels uncanny</H2>
      <P>
        The reach-out often lands right when you've started to detach — when you've gone a few
        days without checking, when the pull has loosened. That's not coincidence. The moment you
        begin to move on is the moment the connection feels most at risk to them, and the return
        re-establishes it. You're left feeling crazy for having "almost given up," when in fact
        you were simply healing.
      </P>

      <CTAButton />

      <H2>Why "they came back" doesn't mean what you hope it means</H2>
      <P>
        A return after silence can feel like proof they care — they chose you again. But a pattern
        of leaving and returning isn't choosing; it's a rhythm. Genuine repair looks like the
        silence not happening again. Intermittent return looks like the same cycle, slightly
        rephrased. The question isn't "did they come back" — it's "did anything actually change."
      </P>

      <H2>Seeing the cycle instead of riding it</H2>
      <P>
        The reason this is so disorienting is that each individual reach-out looks warm, even
        loving, on its own. The pattern only becomes visible when you line the moments up — the
        leaving, the gap, the return, the calm, the leaving again. Laid out in sequence, the
        rhythm names itself.
      </P>
      <P>
        If you want to see it on the page —{" "}
        <InlineLink to="/manipulative-text-message-analysis">
          how this shows up in the actual messages →
        </InlineLink>
      </P>

      <H2>Further reading</H2>
      <ul className="mt-4 space-y-3 list-none pl-0">
        <li>
          <Link
            to="/why-do-i-feel-exhausted-after-conversations"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            Why do I feel exhausted after conversations →
          </Link>
        </li>
        <li>
          <Link
            to="/manipulative-text-message-analysis"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            Manipulative text message analysis →
          </Link>
        </li>
        <li>
          <Link
            to="/darvo-examples"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            DARVO examples →
          </Link>
        </li>
        <li>
          <Link
            to="/articulation-before-adjudication"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            Articulation before adjudication →
          </Link>
        </li>
      </ul>

      <P>
        The pattern is already there in the messages — the leaving and the returning, on a loop
        you didn't choose. Seeing it laid out is often the moment it stops running you. This is{" "}
        <InlineLink to="/articulation-before-adjudication">
          the emerging category these tools belong to →
        </InlineLink>
      </P>

      <CTAButton />
    </SeoPageShell>
  );
};

export default WhyDoTheyDisappearAndComeBack;
